import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

function getCookie(name) {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

function setCookie(name, value, days) {
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value};expires=${d.toUTCString()};path=/;SameSite=Lax`;
}

const COOKIE_NAME = 'flynaut_consent';
const CONSENT_DURATION = 365;

const defaultPreferences = {
  essential: true,
  analytics: false,
  marketing: false,
  functional: false,
};

const categories = [
  {
    key: 'essential',
    label: 'Essential',
    locked: true,
    description: 'Required for the website to function. Session management, security tokens, preferences.',
  },
  {
    key: 'analytics',
    label: 'Analytics',
    locked: false,
    description: 'Google Analytics, heatmaps. Helps us understand how visitors use the site.',
  },
  {
    key: 'marketing',
    label: 'Marketing',
    locked: false,
    description: 'HubSpot tracking, LinkedIn Insight Tag, retargeting pixels.',
  },
  {
    key: 'functional',
    label: 'Functional',
    locked: false,
    description: 'Chat widget preferences, language preferences, A/B testing.',
  },
];

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [preferences, setPreferences] = useState({ ...defaultPreferences });
  const [expandedCategory, setExpandedCategory] = useState(null);

  useEffect(() => {
    const existing = getCookie(COOKIE_NAME);
    if (!existing) {
      // Delay banner appearance slightly for smoother UX
      const timer = setTimeout(() => setShowBanner(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (prefs) => {
    setCookie(COOKIE_NAME, JSON.stringify(prefs), CONSENT_DURATION);
    setShowBanner(false);
    setShowModal(false);
  };

  const handleAcceptAll = () => {
    const all = { essential: true, analytics: true, marketing: true, functional: true };
    saveConsent(all);
  };

  const handleDecline = () => {
    saveConsent({ ...defaultPreferences });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePref = (key) => {
    if (key === 'essential') return;
    setPreferences((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Banner */}
      {!showModal && (
        <div
          data-testid="cookie-banner"
          className="fixed bottom-0 left-0 right-0 z-[2000] bg-[#1A202C] shadow-2xl animate-slide-up"
        >
          <div className="max-w-content mx-auto px-6 py-4">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="flex-1">
                <p className="text-[#A0AEC0] text-sm leading-relaxed">
                  We use cookies to improve your experience and analyze site traffic.{' '}
                  <a href="/cookies" className="underline text-[#A0AEC0] hover:text-white transition-colors">
                    Learn more in our Cookie Policy.
                  </a>
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  onClick={() => setShowModal(true)}
                  data-testid="cookie-manage-btn"
                  className="border border-white/30 text-white hover:border-white/60 rounded-full px-5 py-2 text-sm font-medium transition-all"
                >
                  Manage Preferences
                </button>
                <button
                  onClick={handleAcceptAll}
                  data-testid="cookie-accept-btn"
                  className="bg-orange-500 hover:bg-orange-600 text-white rounded-full px-5 py-2 text-sm font-medium transition-colors"
                >
                  Accept All
                </button>
              </div>
              <button
                onClick={handleDecline}
                data-testid="cookie-decline-btn"
                className="text-[#718096] hover:text-white text-xs transition-colors md:ml-0 hidden md:block"
              >
                Decline Non-Essential
              </button>
              <button
                onClick={handleDecline}
                className="text-[#718096] hover:text-white text-xs transition-colors md:hidden"
              >
                Decline Non-Essential
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preferences Modal */}
      {showModal && (
        <>
          <div
            className="fixed inset-0 bg-[#1A202C]/50 z-[2001]"
            onClick={() => setShowModal(false)}
          />
          <div
            data-testid="cookie-modal"
            className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[2002] bg-white rounded-2xl shadow-2xl w-[90%] max-w-[500px] max-h-[80vh] overflow-y-auto"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-navy-800 font-bold text-lg">Cookie Preferences</h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-navy-400 hover:text-navy-600 transition-colors"
                  aria-label="Close preferences"
                >
                  <X size={20} />
                </button>
              </div>
              <p className="text-navy-600 text-sm leading-relaxed mb-6">
                Choose which cookies you want to allow. Essential cookies cannot be disabled as they are required for the website to function.
              </p>

              <div className="space-y-3">
                {categories.map((cat) => (
                  <div key={cat.key} className="border border-navy-100 rounded-xl overflow-hidden">
                    <div className="flex items-center justify-between p-4">
                      <button
                        onClick={() => setExpandedCategory(expandedCategory === cat.key ? null : cat.key)}
                        className="flex-1 text-left"
                      >
                        <span className="text-navy-800 font-semibold text-sm">{cat.label}</span>
                        {cat.locked && (
                          <span className="ml-2 text-navy-400 text-xs">Always on</span>
                        )}
                      </button>
                      {/* Toggle */}
                      <button
                        onClick={() => togglePref(cat.key)}
                        data-testid={`cookie-toggle-${cat.key}`}
                        disabled={cat.locked}
                        className={`relative w-10 h-5 rounded-full transition-colors duration-200 flex-shrink-0 ${
                          preferences[cat.key] ? 'bg-orange-500' : 'bg-navy-200'
                        } ${cat.locked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                        aria-label={`Toggle ${cat.label} cookies`}
                      >
                        <span
                          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform duration-200 ${
                            preferences[cat.key] ? 'translate-x-[22px]' : 'translate-x-[2px]'
                          }`}
                        />
                      </button>
                    </div>
                    {expandedCategory === cat.key && (
                      <div className="px-4 pb-4 pt-0 border-t border-navy-100">
                        <p className="text-navy-500 text-xs leading-relaxed pt-3">{cat.description}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={handleSavePreferences}
                data-testid="cookie-save-btn"
                className="mt-6 w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full py-3 text-sm font-medium transition-colors"
              >
                Save Preferences
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
